/**
 * StimResponseChart — read-only follicle-growth chart for issue #2001.
 *
 * Shows follicle size objectively without judging "maturity": a growth bar per
 * monitoring day (height = lead follicle, shaded on a continuous size scale so
 * bigger follicles read deeper, each follicle marked, count on top), a plain mm
 * axis with neutral gridlines, and estradiol as a soft rising tide behind the
 * bars. The trigger threshold is clinic- and patient-specific, so the chart
 * reports the measurements and leaves that call to the care team.
 */

import { useMemo, useState } from "react";
import { View, Text, Pressable } from "react-native";
import Svg, {
  Rect,
  Line,
  Path,
  Circle,
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
  Text as SvgText,
  G,
} from "react-native-svg";
import {
  useTestResults,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import type { ApiPhase } from "@src/packages/patients/hooks/usePhases";
import { useTranslations } from "@src/packages/i18n";
import type { CycleMonitoringEncounter } from "@src/packages/care/hooks/useCycleMonitoring";
import {
  monitoringDisplayResultKey,
  monitoringEncounterDisplayResults,
  monitoringResultsForCycle,
  type MonitoringDisplayResult,
} from "./stimMonitoringEntry";
import { EMBER } from "../../../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const E2_COLOR = "#B84A16"; // estradiol tide
const LINING_COLOR = "#6B8E9B"; // endometrial lining (blue-grey)
const TEXT_MID = "#5A4D45";
const TEXT_LIGHT = "#8A7D74";

// Continuous size scale (small = pale, large = deep). Deliberately not a
// maturity judgment — just "bigger follicles read deeper".
const RAMP_FROM = [214, 197, 176]; // #D6C5B0
const RAMP_TO = [122, 74, 34]; // #7A4A22
const RAMP_MIN_MM = 8;
const RAMP_MAX_MM = 20;

const VB_W = 340;
const VB_H = 210;
const PAD_L = 22;
const PAD_R = 34;
const PAD_T = 22;
const PAD_B = 26;
const PLOT_W = VB_W - PAD_L - PAD_R;
const PLOT_H = VB_H - PAD_T - PAD_B;

type ChartDay = {
  dateISO: string;
  dayLabel: string;
  sizes: number[];
  lead: number | null;
  e2: number | null;
  lining: number | null;
  isTrigger: boolean;
};

interface StimResponseChartProps {
  cycleId: string | null | undefined;
  cycleStartDate: string | null | undefined;
  phases: ApiPhase[];
  monitoringEncounters?: CycleMonitoringEncounter[];
  includeUnlinkedResults?: boolean;
}

function sizeColor(mm: number): string {
  const tt = Math.max(
    0,
    Math.min(1, (mm - RAMP_MIN_MM) / (RAMP_MAX_MM - RAMP_MIN_MM)),
  );
  const c = RAMP_FROM.map((f, i) => Math.round(f + (RAMP_TO[i]! - f) * tt));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function parseSizes(raw: string | null | undefined): number[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(",")
    .map((s) => parseFloat(s.trim()))
    .filter((n) => !Number.isNaN(n));
}

function resolvedValue(tr: MonitoringDisplayResult): string {
  return tr.doctorValue ?? tr.patientValue ?? tr.value ?? "";
}

function stimDayNumber(dateISO: string, referenceStart: string): number {
  const day = new Date(dateISO + "T00:00:00").getTime();
  const start = new Date(referenceStart + "T00:00:00").getTime();
  const offset = Math.floor((day - start) / 86400000);
  return offset >= 0 ? offset + 1 : offset;
}

const SIZES_LEFT = /follicle\s*sizes?\s*left/i;
const SIZES_RIGHT = /follicle\s*sizes?\s*right/i;
const E2_PATTERN = /estradiol|^e2$/i;
const LINING_PATTERN = /endometrial|lining|thickness/i;

/** The three things this chart can draw, each one switchable on its own. */
type SeriesKey = "size" | "e2" | "lining";

export default function StimResponseChart({
  cycleId,
  cycleStartDate,
  phases,
  monitoringEncounters,
  includeUnlinkedResults = false,
}: StimResponseChartProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("StimResponseChart");
  const { data: testResults } = useTestResults();

  const days = useMemo<ChartDay[]>(() => {
    const acc = new Map<
      string,
      {
        sizes: number[];
        sizesFromResult: boolean;
        e2: number | null;
        lining: number | null;
      }
    >();
    const encounterResults = monitoringEncounterDisplayResults(
      monitoringEncounters,
      cycleId,
    );
    const encounterResultKeys = new Set(
      encounterResults.map(monitoringDisplayResultKey),
    );
    const ensure = (dateISO: string) => {
      let a = acc.get(dateISO);
      if (!a) {
        a = { sizes: [], sizesFromResult: false, e2: null, lining: null };
        acc.set(dateISO, a);
      }
      return a;
    };

    const ingest = (
      dateISO: string,
      name: string,
      raw: string,
      collectSizes: boolean,
    ) => {
      const a = ensure(dateISO);
      if (E2_PATTERN.test(name)) {
        const num = parseFloat(raw);
        if (!Number.isNaN(num) && a.e2 == null) {
          a.e2 = num;
        }
      } else if (SIZES_LEFT.test(name) || SIZES_RIGHT.test(name)) {
        a.sizes.push(...parseSizes(raw));
        a.sizesFromResult = collectSizes;
      } else if (LINING_PATTERN.test(name)) {
        const num = parseFloat(raw);
        if (!Number.isNaN(num) && a.lining == null) {
          a.lining = num;
        }
      }
    };

    for (const tr of encounterResults) {
      ingest(tr.date!.split("T")[0]!, tr.name ?? "", resolvedValue(tr), true);
    }
    for (const tr of monitoringResultsForCycle(testResults, cycleId, {
      cycleStartDate,
      includeUnlinkedResults,
    })) {
      if (!tr.date) continue;
      if (encounterResultKeys.has(monitoringDisplayResultKey(tr))) continue;
      ingest(tr.date.split("T")[0]!, tr.name ?? "", resolvedValue(tr), true);
    }
    for (const phase of phases ?? []) {
      for (const tr of phase.testResults ?? []) {
        if (!tr.date) {
          continue;
        }
        if (encounterResultKeys.has(monitoringDisplayResultKey(tr))) continue;
        ingest(
          tr.date.split("T")[0]!,
          tr.name ?? "",
          resolvedValue(tr as TestResult),
          true,
        );
      }
      for (const f of phase.ovaryFollicles ?? []) {
        if (!f.date || f.size == null) {
          continue;
        }
        const a = ensure(f.date.split("T")[0]!);
        if (!a.sizesFromResult) {
          a.sizes.push(f.size);
        }
      }
      const phaseDate = phase.startDate?.split("T")[0];
      if (phaseDate) {
        for (const m of phase.fertilityMetrics ?? []) {
          if (m.type === "endometrial_thickness" && m.value != null) {
            const a = ensure(phaseDate);
            if (a.lining == null) {
              a.lining = m.value;
            }
          }
        }
      }
    }

    const triggerDates = new Set<string>();
    for (const phase of phases ?? []) {
      const d = phase.triggerShot?.date;
      if (d) {
        triggerDates.add(d.split("T")[0]!);
      }
    }

    const dates = [...acc.keys()].filter((d) => {
      const a = acc.get(d)!;
      return a.sizes.length > 0 || a.e2 != null || a.lining != null;
    });
    if (dates.length === 0) {
      return [];
    }
    const referenceStart =
      cycleStartDate ?? dates.reduce((a, b) => (a < b ? a : b));
    dates.sort();

    return dates.map((dateISO) => {
      const a = acc.get(dateISO)!;
      const sizes = [...a.sizes].sort((x, y) => y - x);
      return {
        dateISO,
        dayLabel: `D${stimDayNumber(dateISO, referenceStart)}`,
        sizes,
        lead: sizes.length > 0 ? sizes[0]! : null,
        e2: a.e2,
        lining: a.lining,
        isTrigger: triggerDates.has(dateISO),
      };
    });
  }, [
    testResults,
    phases,
    cycleId,
    cycleStartDate,
    monitoringEncounters,
    includeUnlinkedResults,
  ]);

  /**
   * Which series are switched on.
   *
   * Three quantities on two axes in one small plot is a lot to read at once,
   * and the reader is the one who knows which question she is asking today —
   * "are the follicles growing" and "is the estradiol climbing" want different
   * pictures. So the legend is a set of switches rather than a caption.
   *
   * Declared above the empty-state return so the hook order cannot change with
   * the data.
   */
  const [shown, setShown] = useState<Record<SeriesKey, boolean>>({
    size: true,
    e2: true,
    lining: true,
  });

  const hasFollicles = days.some((d) => d.sizes.length > 0);
  if (days.length === 0 || !hasFollicles) {
    return (
      <View style={{ ...GLASS_CARD_STYLE, padding: 20, marginTop: 12 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 14, fontWeight: "600", color: EMBER.colors.text }}
        >
          {t("care.stim_response_chart.empty_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: TEXT_LIGHT,
            marginTop: 6,
            lineHeight: 17,
          }}
        >
          {t("care.stim_response_chart.empty_subtitle")}
        </Text>
      </View>
    );
  }

  const n = days.length;
  const hasE2 = days.some((d) => d.e2 != null);
  const hasLining = days.some((d) => d.lining != null);

  /**
   * Only a series with something to show gets a switch. A toggle for a
   * measurement the clinic never recorded is a control that does nothing.
   */
  const availableSeries: SeriesKey[] = [
    ...(hasFollicles ? (["size"] as const) : []),
    ...(hasE2 ? (["e2"] as const) : []),
    ...(hasLining ? (["lining"] as const) : []),
  ];
  const showSize = shown.size && hasFollicles;
  const showE2 = shown.e2 && hasE2;
  const showLining = shown.lining && hasLining;

  const toggleSeries = (key: SeriesKey) => {
    setShown((current) => {
      const next = { ...current, [key]: !current[key] };
      // The last visible series cannot be switched off: an empty plot is not a
      // view of anything, and the way back out of one is not obvious.
      return availableSeries.some((k) => next[k]) ? next : current;
    });
  };
  const allSizes = days.flatMap((d) => d.sizes);
  /**
   * The left axis is millimetres, and both the follicles and the endometrium
   * are measured in millimetres — so both have to fit inside it. The lining was
   * left out of this maximum, which held as long as it stayed under a follicle:
   * a thick lining would have drawn its line off the top of the plot.
   *
   * Computed over every day rather than the shown ones, so switching a series
   * off does not rescale the axis under the one still being read.
   */
  const allLining = days
    .map((d) => d.lining)
    .filter((v): v is number => v != null);
  const sizeMax = Math.max(
    20,
    Math.ceil(Math.max(...allSizes, ...allLining, 18) / 5) * 5,
  );
  const e2Values = days.map((d) => d.e2).filter((v): v is number => v != null);
  const e2Max = showE2 && e2Values.length > 0 ? Math.max(...e2Values) * 1.2 : 0;

  /**
   * The mark's width, which is also a single measurement's diameter.
   *
   * Kept deliberately modest. The width is derived from the space each day
   * gets, so a short cycle used to inflate the marks to 30 — a circle wider
   * than the gap between two gridlines, reading as a huge follicle when it only
   * meant "few days recorded". A mark should say something about the follicles,
   * not about how much room the chart happens to have.
   */
  const barW = Math.min(22, Math.max(12, (PLOT_W / n) * 0.45));

  /**
   * Days sit on a band, not on the plot's edges.
   *
   * The first day used to be centred exactly on PAD_L and the last on the
   * plot's right edge, so half of every end mark — up to 15px of a 30px
   * capsule — hung outside the plot, over the axis numbers and past the card.
   * Two days is the worst case, because that is when barW is widest.
   *
   * Half a mark plus a hair of air is reserved at each end, so a mark is inside
   * the plot it belongs to whatever the number of days.
   */
  const xInset = barW / 2 + 4;
  const xSpan = Math.max(PLOT_W - 2 * xInset, 0);
  const xFor = (i: number) =>
    n === 1 ? PAD_L + PLOT_W / 2 : PAD_L + xInset + (i / (n - 1)) * xSpan;
  const sizeY = (mm: number) => PAD_T + PLOT_H - (mm / sizeMax) * PLOT_H;
  const e2Y = (v: number) =>
    e2Max > 0 ? PAD_T + PLOT_H - (v / e2Max) * PLOT_H : PAD_T + PLOT_H;
  const baseY = PAD_T + PLOT_H;

  const sizeTicks = [0, 5, 10, 15, 20, 25].filter((v) => v <= sizeMax);

  // Estradiol tide (soft area + thin line) behind the bars.
  const e2Pts = (showE2 ? days : [])
    .map((d, i) => (d.e2 != null ? { x: xFor(i), y: e2Y(d.e2) } : null))
    .filter((p): p is { x: number; y: number } => p != null);
  const buildSmooth = (pts: { x: number; y: number }[]) => {
    if (pts.length < 2) {
      return "";
    }
    let path = `M${pts[0]!.x},${pts[0]!.y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const c = pts[i]!;
      const nx = pts[i + 1]!;
      const mid = (c.x + nx.x) / 2;
      path += ` C${mid},${c.y} ${mid},${nx.y} ${nx.x},${nx.y}`;
    }
    return path;
  };
  const e2Line = buildSmooth(e2Pts);
  const e2Area =
    e2Pts.length >= 2
      ? `${e2Line} L${e2Pts[e2Pts.length - 1]!.x},${baseY} L${e2Pts[0]!.x},${baseY} Z`
      : "";

  // Endometrial lining shares the left mm axis (it is also an mm measurement).
  const liningPts = (showLining ? days : [])
    .map((d, i) =>
      d.lining != null ? { x: xFor(i), y: sizeY(d.lining) } : null,
    )
    .filter((p): p is { x: number; y: number } => p != null);
  const liningLine = buildSmooth(liningPts);

  const sizeSwatch = (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 2 }}>
      {[9, 14, 19].map((mm, i) => (
        <View
          key={mm}
          style={{
            width: 8 + i,
            height: 8 + i,
            borderRadius: (8 + i) / 2,
            backgroundColor: sizeColor(mm),
          }}
        />
      ))}
    </View>
  );

  /**
   * What the millimetre axis is currently measuring.
   *
   * It used to say "Follicle mm" whether or not the follicles were shown, and
   * once follicles could be switched off it said nothing at all — leaving the
   * endometrium reading off an unlabelled scale. Naming both is also the plain
   * way to show that one axis serves them both.
   */
  const mmAxisCaption = (): string => {
    if (showSize && showLining) {
      return t("care.stim_response_chart.axis_follicles_and_lining_mm");
    }
    if (showSize) return t("care.stim_response_chart.axis_size");
    if (showLining) return t("care.stim_response_chart.axis_lining_mm");
    return "";
  };

  const legendEntries = availableSeries.map((key) => {
    if (key === "e2") {
      return {
        key,
        label: t("care.stim_response_chart.legend_e2"),
        isOn: showE2,
        swatch: (
          <View
            style={{
              width: 14,
              height: 8,
              borderRadius: 3,
              backgroundColor: `${E2_COLOR}33`,
            }}
          />
        ),
      };
    }
    if (key === "lining") {
      return {
        key,
        label: t("care.stim_response_chart.legend_lining"),
        isOn: showLining,
        swatch: (
          <View
            style={{
              width: 14,
              height: 2.5,
              borderRadius: 1.5,
              backgroundColor: LINING_COLOR,
            }}
          />
        ),
      };
    }
    return {
      key,
      label: t("care.stim_response_chart.legend_size"),
      isOn: showSize,
      swatch: sizeSwatch,
    };
  });

  return (
    <View style={{ ...GLASS_CARD_STYLE, padding: 16, marginTop: 12, gap: 12 }}>
      {/* The legend is the control: each entry switches its own series. */}
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {legendEntries.map((entry) => (
          <Pressable
            key={entry.key}
            onPress={() => {
              // Every legend entry funnels through here; its label is
              // translated copy, so pair it with a stable action.
              capturePress(pressLabel(entry.label), "chart_series_legend");
              toggleSeries(entry.key);
            }}
            accessibilityRole="button"
            accessibilityLabel={entry.label}
            accessibilityState={{ selected: entry.isOn }}
            hitSlop={6}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingVertical: 5,
              paddingHorizontal: 10,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: entry.isOn ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.05)",
              backgroundColor: entry.isOn
                ? "rgba(255,255,255,0.55)"
                : "transparent",
            }}
          >
            {/* The swatch fades rather than disappearing, so an off series
                still says which colour it would come back as. */}
            <View style={{ opacity: entry.isOn ? 1 : 0.28 }}>
              {entry.swatch}
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                fontWeight: "600",
                color: entry.isOn ? TEXT_MID : TEXT_LIGHT,
              }}
            >
              {entry.label}
            </Text>
          </Pressable>
        ))}
      </View>

      <Svg width="100%" height={VB_H} viewBox={`0 0 ${VB_W} ${VB_H}`}>
        <Defs>
          {days.map((d, i) =>
            d.lead != null ? (
              <SvgLinearGradient
                key={`g-${i}`}
                id={`bar${i}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <Stop
                  offset="0%"
                  stopColor={sizeColor(d.lead)}
                  stopOpacity="0.92"
                />
                <Stop
                  offset="100%"
                  stopColor={sizeColor(d.lead)}
                  stopOpacity="0.5"
                />
              </SvgLinearGradient>
            ) : null,
          )}
          <SvgLinearGradient id="e2Grad" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor={E2_COLOR} stopOpacity="0.16" />
            <Stop offset="100%" stopColor={E2_COLOR} stopOpacity="0" />
          </SvgLinearGradient>
        </Defs>

        {/* Neutral mm gridlines + axis labels */}
        {sizeTicks.map((mm) => (
          <G key={`grid-${mm}`}>
            {mm > 0 ? (
              <Line
                x1={PAD_L}
                y1={sizeY(mm)}
                x2={VB_W - PAD_R}
                y2={sizeY(mm)}
                stroke="rgba(0,0,0,0.05)"
                strokeWidth={1}
              />
            ) : null}
            {/* The numbers go with the caption: with neither millimetre series
                shown, a 0-25 scale down the left edge measures nothing on the
                plot. The gridlines stay as the plot's frame. */}
            {showSize || showLining ? (
              <SvgText
                x={PAD_L - 5}
                y={sizeY(mm) + 3}
                textAnchor="end"
                fontSize={8}
                fontFamily="DMSans_400Regular"
                fill="rgba(0,0,0,0.28)"
              >
                {mm}
              </SvgText>
            ) : null}
          </G>
        ))}
        <SvgText
          x={PAD_L - 5}
          y={PAD_T - 8}
          textAnchor="start"
          fontSize={7.5}
          fontFamily="DMSans_400Regular"
          fill="rgba(0,0,0,0.3)"
        >
          {mmAxisCaption()}
        </SvgText>

        {/* Estradiol right axis (so the tide is readable, not just a shape) */}
        {e2Max > 0 ? (
          <G>
            {[0, e2Max / 2, e2Max].map((v, i) => (
              <SvgText
                key={`e2y-${i}`}
                x={VB_W - PAD_R + 4}
                y={e2Y(v) + 3}
                textAnchor="start"
                fontSize={7.5}
                fontFamily="DMSans_400Regular"
                fill={`${E2_COLOR}AA`}
              >
                {Math.round(v) >= 1000
                  ? `${(v / 1000).toFixed(1)}k`
                  : String(Math.round(v))}
              </SvgText>
            ))}
            <SvgText
              x={VB_W - PAD_R + 4}
              y={PAD_T - 8}
              textAnchor="start"
              fontSize={7.5}
              fontFamily="DMSans_400Regular"
              fill={`${E2_COLOR}AA`}
            >
              E2
            </SvgText>
          </G>
        ) : null}

        {/* Estradiol tide */}
        {e2Area ? <Path d={e2Area} fill="url(#e2Grad)" /> : null}
        {e2Line ? (
          <Path
            d={e2Line}
            fill="none"
            stroke={E2_COLOR}
            strokeOpacity={0.5}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}

        {/* Follicle-range capsules: each bar spans the smallest to the
            largest follicle measured that day (the cohort range), so you see
            the spread climb over time. Top of the capsule is the lead. */}
        {days.map((d, i) => {
          if (!showSize || d.lead == null || d.sizes.length === 0) {
            return null;
          }
          const cx = xFor(i);
          const minV = d.sizes[d.sizes.length - 1]!;
          const leadY = sizeY(d.lead);
          let yTop = leadY;
          let capH = sizeY(minV) - yTop;

          /**
           * Whether this day measured a range at all, in millimetres.
           *
           * Asked of the measurements rather than of the drawing. The shape
           * question below — is this capsule too short to look like one — is a
           * question about pixels, and the two were once the same test: a
           * capsule shorter than it is wide was assumed to mean "one
           * follicle". That held while the mark was 9px tall, but the mark is
           * now up to 22px against a 162px plot, so on a 25mm axis it declared
           * anything under a 3.4mm spread a single point. A day with follicles
           * at 20 and 17mm is a real cohort, and it was being drawn as one
           * measurement.
           */
          const hasSpread = d.lead - minV >= 0.5;

          /**
           * A capsule shorter than it is wide is drawn round.
           *
           * Its ends are rounded by half its width, so at that height it is a
           * squashed ellipse — which reads as a measurement squeezed flat
           * rather than a small spread. Rounding it out keeps it in the same
           * family as the capsule it replaces. The range it stands for is
           * still told by the lead marker, which is placed on the follicle's
           * own position rather than on the inflated top.
           */
          if (capH < barW) {
            const mid = (leadY + sizeY(minV)) / 2;
            yTop = mid - barW / 2;
            capH = barW;
          }
          return (
            <G key={`bar-${d.dateISO}`}>
              <Rect
                x={cx - barW / 2}
                y={yTop}
                width={barW}
                height={capH}
                rx={barW / 2}
                fill={`url(#bar${i})`}
              />
              {/* Lead marker, only when there is a range for it to head. On a
                  single measurement it would draw a chord across a circle and
                  claim a largest follicle among one. Drawn at the lead's own
                  height, not the mark's top, so a rounded-out capsule does not
                  move the measurement it reports. */}
              {hasSpread ? (
                <Line
                  x1={cx - barW / 2 + 3}
                  y1={leadY + 2}
                  x2={cx + barW / 2 - 3}
                  y2={leadY + 2}
                  stroke="rgba(255,255,255,0.65)"
                  strokeWidth={1.6}
                />
              ) : null}
              {/* Follicle count above the capsule */}
              <SvgText
                x={cx}
                y={yTop - 5}
                textAnchor="middle"
                fontSize={10}
                fontFamily="DMSans_400Regular"
                fontWeight="700"
                fill={sizeColor(d.lead)}
              >
                {d.sizes.length}
              </SvgText>
            </G>
          );
        })}

        {/* Endometrial lining line (mm, on the left axis) */}
        {liningLine ? (
          <Path
            d={liningLine}
            fill="none"
            stroke={LINING_COLOR}
            strokeWidth={1.8}
            strokeDasharray="4,3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.9}
          />
        ) : null}
        {liningPts.map((p, i) => (
          <Circle
            key={`ln-${i}`}
            cx={p.x}
            cy={p.y}
            r={2.2}
            fill={LINING_COLOR}
          />
        ))}

        {/* Estradiol points */}
        {e2Pts.map((p, i) => (
          <Circle
            key={`e2p-${i}`}
            cx={p.x}
            cy={p.y}
            r={2.4}
            fill={E2_COLOR}
            fillOpacity={0.7}
          />
        ))}

        {/* Trigger day marker */}
        {days.map((d, i) =>
          d.isTrigger ? (
            <G key={`trig-${d.dateISO}`}>
              <Line
                x1={xFor(i)}
                y1={PAD_T}
                x2={xFor(i)}
                y2={baseY}
                stroke={E2_COLOR}
                strokeWidth={1}
                strokeDasharray="3,3"
                opacity={0.5}
              />
              <SvgText
                x={xFor(i)}
                y={PAD_T - 1}
                textAnchor="middle"
                fontSize={7.5}
                fontFamily="DMSans_400Regular"
                fontWeight="700"
                fill={E2_COLOR}
              >
                {t("care.stim_response_chart.trigger")}
              </SvgText>
            </G>
          ) : null,
        )}

        {/* X-axis day labels */}
        {days.map((d, i) => (
          <SvgText
            key={`x-${d.dateISO}`}
            x={xFor(i)}
            y={VB_H - 8}
            textAnchor="middle"
            fontSize={10}
            fontFamily="DMSans_400Regular"
            fontWeight={d.isTrigger ? "700" : "500"}
            fill={d.isTrigger ? E2_COLOR : "rgba(0,0,0,0.5)"}
          >
            {d.dayLabel}
          </SvgText>
        ))}
      </Svg>
    </View>
  );
}
